/**
 * BracketOverlay
 *
 * A Phaser-based visual overlay that renders a bracket around a looped section
 * of sheet music, along with a semi-transparent dimming layer over the area
 * outside the bracket. Used in the Repertoire Play Screen when looping is active.
 *
 * The bracket is drawn as two vertical lines (start/end) connected by horizontal
 * lines at top and bottom, forming a visual `[    ]` shape. Handles at each edge
 * are rendered as interactive drag targets for resizing the bracket.
 *
 * The dimming overlay covers all score area outside the bracket bounds with a
 * semi-transparent dark layer, drawing attention to the looped region.
 *
 * Interaction (Phase 3.1):
 * - Drag individual handles to resize the bracket (start or end edge)
 * - Drag the bracket body to move the entire bracket
 * - Handles swap roles automatically if dragged past each other
 * - After drag ends, edges snap to note onset/offset boundaries
 * - Minimum bracket size enforced (at least 2 notes)
 * - Bracket position changes are debounced before persistence
 */

import Phaser from 'phaser';
import { snapToNearestNoteOnset, snapToNearestNoteOffset } from 'Utils/BracketPlacement';
import { isCapacitorNative } from 'Utils/Constants';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Layout information for a single phrase, used to position the bracket
 * and dimming overlay relative to the rendered score.
 */
export interface PhraseLayoutInfo {
  /** Index of this phrase in the scene's phrases array */
  phraseIndex: number;
  /** World-space Y coordinate of the phrase container */
  y: number;
  /** Scaled height of the phrase */
  height: number;
  /** Note positions within this phrase, sorted by timestamp */
  notePositions: Array<{ timestamp: number; x: number; width: number }>;
  /** World-space X of each measure's right barline within this phrase, sorted */
  measureBarlineXPositions: number[];
  /** World-space X of the phrase container's left edge */
  containerLeftEdge: number;
  /** World-space X of the phrase container's right edge */
  containerRightEdge: number;
  /** Global start timestamp of this phrase (measure start, may be a rest) */
  phraseStartTimestamp: number;
}

/**
 * Describes a rectangular region in world space for bracket rendering.
 * Used internally to compute bracket geometry across phrase rows.
 */
interface BracketRow {
  /** World-space X of the bracket left edge on this row */
  startX: number;
  /** World-space X of the bracket right edge on this row */
  endX: number;
  /** World-space Y of the row top */
  topY: number;
  /** World-space Y of the row bottom */
  bottomY: number;
}

/**
 * Callback invoked when the bracket position changes due to user interaction.
 * Receives the new start and end timestamps after snapping and validation.
 */
type BracketChangedCallback = (startTimestamp: number, endTimestamp: number) => void;

/**
 * Data required for snapping and constraint validation during bracket interaction.
 */
export interface BracketNoteData {
  /** Sorted, deduplicated array of all note onset timestamps */
  noteOnsets: number[];
  /** Sorted, deduplicated array of all note offset timestamps */
  noteOffsets: number[];
  /**
   * For each onset, the MAX offset across all notes that share that onset
   * (e.g. the longest note in a chord). Used by the bracket-end snap so that
   * the bracket ends when the LONGEST note in the last in-bracket chord
   * finishes sounding — not the shortest, which would restart the loop early.
   */
  maxOffsetByOnset: Map<number, number>;
  /** Sorted array of phrase start timestamps, used to cap re-snapped bracket
   *  ends at phrase boundaries when shifting. */
  phraseStartTimestamps: number[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BRACKET_COLOR = 0x4f96bd;
const BRACKET_ALPHA = 0.8;
const BRACKET_LINE_WIDTH = 3;

/** Handle: circular grab button with < > chevrons inside.
 *  Base values are in CSS pixels; multiplied by DPR at render time
 *  since the Phaser canvas uses device-pixel coordinates. */
const HANDLE_RADIUS_BASE = 18;
const HANDLE_COLOR = 0x6b7fdb;
const HANDLE_ALPHA = 1.0;

/** Chevrons inside the circular handle (base values in CSS pixels) */
const CHEVRON_COLOR = 0xffffff;
const CHEVRON_ALPHA = 1.0;
const CHEVRON_LINE_WIDTH_BASE = 2;
const CHEVRON_HEIGHT_BASE = 7;
const CHEVRON_WIDTH_BASE = 4;
const CHEVRON_GAP_BASE = 3; // gap between the two chevrons < >

/** Scale factor: CSS pixels → device pixels */
function handleDprScale(): number {
  return window.devicePixelRatio ?? 1;
}

const DIMMING_COLOR = 0x1a1a2e;
const DIMMING_ALPHA = 0.4;

/** Horizontal padding added to the bracket edges around the note positions */
const BRACKET_HORIZONTAL_PADDING = 20;

/** Expanded hit area around each handle for easier touch interaction (CSS pixels) */
const HANDLE_HIT_AREA_PADDING_BASE = 14;

/** Minimum number of distinct note onsets required within the bracket */
const MINIMUM_NOTES_IN_BRACKET = 2;

/** Debounce delay in milliseconds before persisting bracket position changes */
const DEBOUNCE_DELAY_MS = 50;

/** Edge-scroll: CSS pixels from viewport top/bottom that trigger auto-scroll.
 *  Set large enough to account for header/footer UI overlapping the canvas. */
const EDGE_SCROLL_ZONE_PX = 150;
/** Edge-scroll speed in camera pixels per frame at the edge */
const EDGE_SCROLL_SPEED = 35;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Count distinct note onsets that fall within the inclusive range
 * [startTimestamp, endTimestamp].
 */
function countOnsetsInRange(
  noteOnsets: number[],
  startTimestamp: number,
  endTimestamp: number,
): number {
  return noteOnsets.filter((onset) => onset >= startTimestamp && onset <= endTimestamp).length;
}

/**
 * Determine whether the range [startTimestamp, endTimestamp] contains
 * at least MINIMUM_NOTES_IN_BRACKET note onsets.
 */
function hasMinimumNotes(
  noteOnsets: number[],
  startTimestamp: number,
  endTimestamp: number,
): boolean {
  return countOnsetsInRange(noteOnsets, startTimestamp, endTimestamp) >= MINIMUM_NOTES_IN_BRACKET;
}

/**
 * Find the phrase whose vertical range contains the given world Y coordinate.
 * Falls back to the nearest phrase if worldY is between or outside all rows.
 */
function findPhraseAtWorldY(
  worldY: number,
  phraseLayout: PhraseLayoutInfo[],
): PhraseLayoutInfo | null {
  if (phraseLayout.length === 0) return null;

  // Check for a direct hit within a phrase's Y range
  const containingPhrase = phraseLayout.find(
    (phrase) => worldY >= phrase.y && worldY <= phrase.y + phrase.height,
  );
  if (containingPhrase) return containingPhrase;

  // No direct hit — find the nearest phrase by vertical distance
  const phrasesByDistance = [...phraseLayout].sort((a, b) => {
    const distanceToA = Math.min(Math.abs(worldY - a.y), Math.abs(worldY - (a.y + a.height)));
    const distanceToB = Math.min(Math.abs(worldY - b.y), Math.abs(worldY - (b.y + b.height)));
    return distanceToA - distanceToB;
  });

  return phrasesByDistance[0];
}

/**
 * Convert a world X position to a timestamp by interpolating among the note
 * positions in the given phrase layout. When a target phrase is provided,
 * only that phrase's note positions are used for the mapping — this prevents
 * incorrect matches when phrases have overlapping X ranges.
 */
function worldXToTimestamp(
  worldX: number,
  phraseLayout: PhraseLayoutInfo[],
  targetPhrase?: PhraseLayoutInfo | null,
): number {
  const allNotes = targetPhrase
    ? targetPhrase.notePositions
    : phraseLayout.flatMap((phrase) => phrase.notePositions);
  if (allNotes.length === 0) return 0;

  // Sort by x position
  const sortedByX = [...allNotes].sort((a, b) => a.x - b.x);

  // If worldX is before all notes, clamp to first note
  if (worldX <= sortedByX[0].x) return sortedByX[0].timestamp;
  // If worldX is past the last note, return slightly past the last onset.
  // This allows the end-handle's "last onset strictly before rawEnd" logic
  // to include the last note when the user drags past it. Without this,
  // rawEnd caps at the last onset and the last note can never be selected.
  if (worldX >= sortedByX[sortedByX.length - 1].x) {
    const lastNote = sortedByX[sortedByX.length - 1];
    // Use 0.001 past the onset — enough for the strict-< check to include it
    return lastNote.timestamp + 0.001;
  }

  // Find the two bracketing notes and interpolate
  for (let noteIndex = 0; noteIndex < sortedByX.length - 1; noteIndex++) {
    const left = sortedByX[noteIndex];
    const right = sortedByX[noteIndex + 1];

    if (worldX >= left.x && worldX <= right.x) {
      const ratio = (worldX - left.x) / (right.x - left.x);
      return left.timestamp + ratio * (right.timestamp - left.timestamp);
    }
  }

  return sortedByX[0].timestamp;
}

/**
 * Find the phrase that contains notes closest to the given timestamp.
 * Since timestamps are sequential across phrases, this identifies which
 * phrase row a timestamp belongs to.
 */
function findPhraseForTimestamp(
  timestamp: number,
  phraseLayout: PhraseLayoutInfo[],
): PhraseLayoutInfo | null {
  if (phraseLayout.length === 0) return null;

  const phrasesWithNotes = phraseLayout.filter((phrase) => phrase.notePositions.length > 0);
  if (phrasesWithNotes.length === 0) return null;

  return phrasesWithNotes.reduce((closest, phrase) => {
    const closestTimestampInPhrase = phrase.notePositions.reduce((nearestNote, note) =>
      Math.abs(note.timestamp - timestamp) < Math.abs(nearestNote.timestamp - timestamp)
        ? note
        : nearestNote,
    );
    const closestTimestampInClosest = closest.notePositions.reduce((nearestNote, note) =>
      Math.abs(note.timestamp - timestamp) < Math.abs(nearestNote.timestamp - timestamp)
        ? note
        : nearestNote,
    );
    return Math.abs(closestTimestampInPhrase.timestamp - timestamp) <
      Math.abs(closestTimestampInClosest.timestamp - timestamp)
      ? phrase
      : closest;
  });
}

/**
 * Convert a timestamp to a world X position by finding the note position
 * with the closest timestamp. When the phrase layout contains multiple phrases,
 * only the phrase that owns the timestamp is searched, avoiding incorrect
 * X values from phrases with overlapping X ranges.
 */
function timestampToWorldX(timestamp: number, phraseLayout: PhraseLayoutInfo[]): number {
  const targetPhrase = findPhraseForTimestamp(timestamp, phraseLayout);
  const allNotes = targetPhrase
    ? targetPhrase.notePositions
    : phraseLayout.flatMap((phrase) => phrase.notePositions);
  if (allNotes.length === 0) return 0;

  let closestNote = allNotes[0];
  let closestDistance = Math.abs(allNotes[0].timestamp - timestamp);

  allNotes.forEach((note) => {
    const distance = Math.abs(note.timestamp - timestamp);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestNote = note;
    }
  });

  return closestNote.x;
}

// ---------------------------------------------------------------------------
// BracketOverlay
// ---------------------------------------------------------------------------

export default class BracketOverlay {
  private scene: Phaser.Scene;
  private bracketGraphics: Phaser.GameObjects.Graphics;
  private dimmingGraphics: Phaser.GameObjects.Graphics;
  private dimmingMaskShape: Phaser.GameObjects.Graphics;
  private dimmingMask: Phaser.Display.Masks.GeometryMask;
  private startHandle: Phaser.GameObjects.Graphics;
  private endHandle: Phaser.GameObjects.Graphics;
  private bracketBodyZone: Phaser.GameObjects.Zone | null = null;
  private isVisible: boolean = false;
  private currentStartTimestamp: number = 0;
  private currentEndTimestamp: number = 0;

  /** Phrase layout cached from the last render, used during drag interactions */
  private cachedPhraseLayout: PhraseLayoutInfo[] = [];

  /** Note onset/offset data for snapping, provided by the scene */
  private noteData: BracketNoteData | null = null;

  /** Callback invoked (after debounce) when bracket position changes */
  private onBracketChanged: BracketChangedCallback | null = null;

  /** Lightweight callback invoked on every drag frame for real-time cursor repositioning */
  private onBracketDragging: ((startTimestamp: number) => void) | null = null;

  /** Previous valid position, used for constraint-violation rollback */
  private previousStartTimestamp: number = 0;
  private previousEndTimestamp: number = 0;

  /** Tracks which element is being dragged */
  private isDraggingStartHandle: boolean = false;
  private isDraggingEndHandle: boolean = false;
  private isDraggingBody: boolean = false;

  /** Drag start offsets for body drag (to maintain relative position) */
  private bodyDragStartWorldX: number = 0;
  private bodyDragStartStartTimestamp: number = 0;
  private bodyDragStartEndTimestamp: number = 0;

  /** Debounce timer for bracket change persistence */
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  /** Edge-scroll: interval ID for the continuous scroll loop */
  private edgeScrollInterval: ReturnType<typeof setInterval> | null = null;

  /** Cached bracket rows from the last render, used during interaction */
  private cachedBracketRows: BracketRow[] = [];

  /** When true, multi-row brackets are merged into a single rectangle
   *  because phrases are laid out end-to-end horizontally on mobile. */
  private mobileView: boolean;

  constructor(scene: Phaser.Scene, mobileView: boolean = false) {
    if (!scene) {
      throw new Error('BracketOverlay requires a valid Phaser.Scene');
    }

    this.scene = scene;
    this.mobileView = mobileView;

    // Create graphics objects — dimming layer renders behind the bracket.
    // An inverted GeometryMask cuts out the bracket polygon so a single
    // full-viewport fill dims everything *except* the selected region.
    this.dimmingGraphics = scene.add.graphics();
    this.dimmingGraphics.setDepth(90);
    this.dimmingGraphics.setVisible(false);

    this.dimmingMaskShape = scene.make.graphics({ add: false });
    this.dimmingMask = this.dimmingMaskShape.createGeometryMask();
    this.dimmingMask.invertAlpha = true;
    this.dimmingGraphics.setMask(this.dimmingMask);

    this.bracketGraphics = scene.add.graphics();
    this.bracketGraphics.setDepth(91);
    this.bracketGraphics.setVisible(false);

    this.startHandle = scene.add.graphics();
    this.startHandle.setDepth(92);
    this.startHandle.setVisible(false);

    this.endHandle = scene.add.graphics();
    this.endHandle.setDepth(92);
    this.endHandle.setVisible(false);

    this.setupHandleInteraction();
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Show the bracket overlay at the given timestamp range.
   * Computes bracket geometry from the phrase layout and renders
   * bracket lines, handles, and dimming overlay.
   */
  show(startTimestamp: number, endTimestamp: number, phraseLayout: PhraseLayoutInfo[]): void {
    if (startTimestamp >= endTimestamp) {
      throw new Error(
        `BracketOverlay.show: startTimestamp (${startTimestamp}) must be less than endTimestamp (${endTimestamp})`,
      );
    }
    if (phraseLayout.length === 0) return;

    this.currentStartTimestamp = startTimestamp;
    this.currentEndTimestamp = endTimestamp;
    this.previousStartTimestamp = startTimestamp;
    this.previousEndTimestamp = endTimestamp;
    this.cachedPhraseLayout = phraseLayout;
    this.isVisible = true;

    this.renderBracketAndOverlay(startTimestamp, endTimestamp, phraseLayout);

    this.bracketGraphics.setVisible(true);
    this.dimmingGraphics.setVisible(true);
    this.startHandle.setVisible(true);
    this.endHandle.setVisible(true);
  }

  /**
   * Hide the bracket overlay and all associated graphics.
   */
  hide(): void {
    this.isVisible = false;
    this.scene.input.setDefaultCursor('default');
    this.bracketGraphics.setVisible(false);
    this.dimmingGraphics.setVisible(false);
    this.startHandle.setVisible(false);
    this.endHandle.setVisible(false);
    this.bracketGraphics.clear();
    this.dimmingGraphics.clear();
    this.dimmingMaskShape.clear();
    this.startHandle.clear();
    this.endHandle.clear();
    this.destroyBodyZone();
    this.clearDebounceTimer();
  }

  /**
   * Update the bracket position without changing visibility.
   * Used when the bracket is dragged/resized or when the layout changes.
   */
  updatePosition(
    startTimestamp: number,
    endTimestamp: number,
    phraseLayout: PhraseLayoutInfo[],
  ): void {
    if (!this.isVisible) return;
    if (startTimestamp >= endTimestamp) return;
    if (phraseLayout.length === 0) return;

    this.currentStartTimestamp = startTimestamp;
    this.currentEndTimestamp = endTimestamp;
    this.cachedPhraseLayout = phraseLayout;

    this.renderBracketAndOverlay(startTimestamp, endTimestamp, phraseLayout);
  }

  /**
   * Get the current bracket start timestamp.
   */
  getStartTimestamp(): number {
    return this.currentStartTimestamp;
  }

  /**
   * Get the current bracket end timestamp.
   */
  getEndTimestamp(): number {
    return this.currentEndTimestamp;
  }

  getRenderedEndX(): number | null {
    if (this.cachedBracketRows.length === 0) return null;
    return this.cachedBracketRows[this.cachedBracketRows.length - 1].endX;
  }

  /**
   * World-space top-right corner of the first (top) bracket row. Used to
   * anchor the perfect-phrase streak badge. Returns null if nothing has
   * been rendered yet.
   */
  getTopRightWorldPosition(): { x: number; y: number } | null {
    if (this.cachedBracketRows.length === 0) return null;
    const topRow = this.cachedBracketRows[0];
    return { x: topRow.endX, y: topRow.topY };
  }

  /**
   * Register a callback to be invoked when the bracket position changes
   * due to user interaction (drag/resize). The callback receives the
   * snapped and validated start and end timestamps.
   */
  setOnBracketChanged(callback: BracketChangedCallback): void {
    this.onBracketChanged = callback;
  }

  /**
   * Register a lightweight callback invoked on every drag frame.
   * Used by the scene to reposition the cursor in real-time as the
   * user drags the bracket handles.
   */
  setOnBracketDragging(callback: (startTimestamp: number) => void): void {
    this.onBracketDragging = callback;
  }

  /**
   * Provide note onset and offset data used for snapping and constraint
   * validation. Must be called before interaction will work correctly.
   */
  setNoteData(noteData: BracketNoteData): void {
    this.noteData = noteData;
  }

  /**
   * Returns true if the bracket overlay is currently handling a drag
   * interaction on any of its elements (handles or body).
   */
  isDragging(): boolean {
    return this.isDraggingStartHandle || this.isDraggingEndHandle || this.isDraggingBody;
  }

  /**
   * Fade handles to a lower opacity during playback so they don't
   * obscure the notes, then restore full opacity when playback stops.
   */
  setHandlesPlaybackAlpha(playing: boolean): void {
    const alpha = playing ? 0.25 : 1;
    this.startHandle.setAlpha(alpha);
    this.endHandle.setAlpha(alpha);
  }

  /**
   * Clean up all graphics objects. Call when the scene is being destroyed.
   */
  destroy(): void {
    this.clearDebounceTimer();
    this.destroyBodyZone();
    this.dimmingGraphics.clearMask(true);
    this.dimmingMaskShape.destroy();
    this.bracketGraphics.destroy();
    this.dimmingGraphics.destroy();
    this.startHandle.destroy();
    this.endHandle.destroy();
  }

  // ---------------------------------------------------------------------------
  // Interaction Setup
  // ---------------------------------------------------------------------------

  /**
   * Set up interactive drag behavior on the start and end handles,
   * and scene-level pointer events for drag tracking.
   */
  private setupHandleInteraction(): void {
    this.setupHandleDragListeners();
    this.setupScenePointerListeners();
  }

  /**
   * Configure each handle graphics as an interactive, draggable Phaser object.
   * Uses an expanded hit area for easier touch targeting.
   */
  private setupHandleDragListeners(): void {
    const hitAreaSize =
      HANDLE_RADIUS_BASE * handleDprScale() * 2 +
      HANDLE_HIT_AREA_PADDING_BASE * handleDprScale() * 2;

    // Start handle interactive zone with grab cursor
    this.startHandle.setInteractive(
      new Phaser.Geom.Rectangle(-hitAreaSize / 2, -hitAreaSize / 2, hitAreaSize, hitAreaSize),
      Phaser.Geom.Rectangle.Contains,
    );
    this.startHandle.on('pointerover', () => {
      this.scene.input.setDefaultCursor('grab');
    });
    this.startHandle.on('pointerout', () => {
      if (!this.isDragging()) this.scene.input.setDefaultCursor('default');
    });

    // End handle interactive zone with grab cursor
    this.endHandle.setInteractive(
      new Phaser.Geom.Rectangle(-hitAreaSize / 2, -hitAreaSize / 2, hitAreaSize, hitAreaSize),
      Phaser.Geom.Rectangle.Contains,
    );
    this.endHandle.on('pointerover', () => {
      this.scene.input.setDefaultCursor('grab');
    });
    this.endHandle.on('pointerout', () => {
      if (!this.isDragging()) this.scene.input.setDefaultCursor('default');
    });

    this.scene.input.setDraggable(this.startHandle);
    this.scene.input.setDraggable(this.endHandle);
  }

  /**
   * Listen for scene-level pointer events to track drag state for handles
   * and the bracket body.
   */
  private setupScenePointerListeners(): void {
    // Handle drag start on handles
    this.scene.input.on(
      'dragstart',
      (_pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.GameObject) => {
        if (gameObject === this.startHandle || gameObject === this.endHandle) {
          this.scene.input.setDefaultCursor('grabbing');
        }
        if (gameObject === this.startHandle) {
          this.isDraggingStartHandle = true;
          this.storePreviousPosition();
          this.startEdgeScrollLoop();
          const dsNoteOnsets = this.noteData?.noteOnsets ?? [];
          const dsSnapped =
            dsNoteOnsets.length > 0
              ? snapToNearestNoteOnset(this.currentStartTimestamp, dsNoteOnsets)
              : this.currentStartTimestamp;
          this.onBracketDragging?.(dsSnapped);
        } else if (gameObject === this.endHandle) {
          this.isDraggingEndHandle = true;
          this.storePreviousPosition();
          this.startEdgeScrollLoop();
          const deNoteOnsets = this.noteData?.noteOnsets ?? [];
          const deSnapped =
            deNoteOnsets.length > 0
              ? snapToNearestNoteOnset(this.currentStartTimestamp, deNoteOnsets)
              : this.currentStartTimestamp;
          this.onBracketDragging?.(deSnapped);
        }
      },
    );

    // Handle drag movement on handles
    this.scene.input.on(
      'drag',
      (
        pointer: Phaser.Input.Pointer,
        gameObject: Phaser.GameObjects.GameObject,
        _dragX: number,
        _dragY: number,
      ) => {
        if (!this.isVisible || !this.noteData) return;

        // Must use getWorldPoint() instead of (pointer.x + camera.scrollX) because
        // on mobile the camera zoom > 1, which makes the raw pointer offset incorrect.
        const worldPoint = this.scene.cameras.main.getWorldPoint(pointer.x, pointer.y);

        if (gameObject === this.startHandle && this.isDraggingStartHandle) {
          this.handleStartHandleDrag(worldPoint.x, worldPoint.y);
        } else if (gameObject === this.endHandle && this.isDraggingEndHandle) {
          this.handleEndHandleDrag(worldPoint.x, worldPoint.y);
        }
      },
    );

    // Handle drag end on handles
    this.scene.input.on(
      'dragend',
      (_pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.GameObject) => {
        if (gameObject === this.startHandle || gameObject === this.endHandle) {
          this.stopEdgeScrollLoop();
          this.finalizeDrag();
          this.isDraggingStartHandle = false;
          this.isDraggingEndHandle = false;
          this.scene.input.setDefaultCursor('grab');
        }
      },
    );
  }

  /**
   * Set up the bracket body zone for drag interaction.
   * Called after rendering to position the zone over the bracket body area.
   */
  private setupBodyZone(rows: BracketRow[]): void {
    if (rows.length === 0) return;
    // On mobile, the body zone (depth 91) covers the bracket interior and
    // intercepts all touch events in that region, preventing horizontal
    // scroll. Skipping it lets the user scroll freely to see the full
    // bracket extent; handle-only resizing is sufficient on touch devices.
    if (this.mobileView) return;

    this.destroyBodyZone();

    // Use the first row to create the body zone
    const firstRow = rows[0];
    const lastRow = rows[rows.length - 1];

    const bodyX = firstRow.startX;
    const bodyY = firstRow.topY;
    const bodyWidth = lastRow.endX - firstRow.startX;
    const bodyHeight = lastRow.bottomY - firstRow.topY;

    if (bodyWidth <= HANDLE_RADIUS_BASE * handleDprScale() * 2) return; // Too narrow for body drag

    this.bracketBodyZone = this.scene.add.zone(
      bodyX + bodyWidth / 2,
      bodyY + bodyHeight / 2,
      bodyWidth - HANDLE_RADIUS_BASE * handleDprScale() * 2, // Exclude handle areas from body zone
      bodyHeight,
    );
    this.bracketBodyZone.setDepth(91);
    this.bracketBodyZone.setInteractive();
    this.scene.input.setDraggable(this.bracketBodyZone);

    // Body drag start
    this.bracketBodyZone.on('dragstart', (pointer: Phaser.Input.Pointer) => {
      if (!this.isVisible) return;
      this.isDraggingBody = true;
      this.storePreviousPosition();
      this.startEdgeScrollLoop();
      // getWorldPoint() for zoom-correct coordinates (see handle drag comment above).
      this.bodyDragStartWorldX = this.scene.cameras.main.getWorldPoint(pointer.x, pointer.y).x;
      this.bodyDragStartStartTimestamp = this.currentStartTimestamp;
      this.bodyDragStartEndTimestamp = this.currentEndTimestamp;
      const bdNoteOnsets = this.noteData?.noteOnsets ?? [];
      const bdSnapped =
        bdNoteOnsets.length > 0
          ? snapToNearestNoteOnset(this.currentStartTimestamp, bdNoteOnsets)
          : this.currentStartTimestamp;
      this.onBracketDragging?.(bdSnapped);
    });

    // Body drag movement
    this.bracketBodyZone.on('drag', (pointer: Phaser.Input.Pointer) => {
      if (!this.isDraggingBody || !this.noteData) return;

      // getWorldPoint() for zoom-correct coordinates (see handle drag comment above).
      const bodyDragWorldPoint = this.scene.cameras.main.getWorldPoint(pointer.x, pointer.y);
      this.handleBodyDrag(bodyDragWorldPoint.x, bodyDragWorldPoint.y);
    });

    // Body drag end
    this.bracketBodyZone.on('dragend', () => {
      if (this.isDraggingBody) {
        this.stopEdgeScrollLoop();
        this.finalizeDrag();
        this.isDraggingBody = false;
        this.scene.input.setDefaultCursor('default');
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Cross-phrase timestamp resolution
  // ---------------------------------------------------------------------------

  /**
   * Convert a world position to a timestamp with cross-phrase wrapping.
   *
   * When the pointer's X extends past the right edge of the phrase at Y,
   * the overflow distance is mapped onto the next phrase(s). Similarly,
   * underflow past the left edge wraps to the previous phrase(s). All
   * phrases share the same horizontal extent, so the overflow maps cleanly.
   *
   * Falls back to the standard (non-wrapping) worldXToTimestamp when the
   * pointer is within the current phrase's horizontal bounds.
   */
  private worldPositionToTimestamp(worldX: number, worldY: number): number {
    const phraseLayout = this.cachedPhraseLayout;
    if (phraseLayout.length === 0) return 0;

    const phrase = findPhraseAtWorldY(worldY, phraseLayout);
    if (!phrase) return 0;

    const phraseWidth = phrase.containerRightEdge - phrase.containerLeftEdge;
    if (phraseWidth <= 0) return worldXToTimestamp(worldX, phraseLayout, phrase);

    if (worldX > phrase.containerRightEdge) {
      const overflow = worldX - phrase.containerRightEdge;
      const extraPhrases = Math.floor(overflow / phraseWidth) + 1;
      const targetIdx = phrase.phraseIndex + extraPhrases;
      const targetPhrase = phraseLayout.find((p) => p.phraseIndex === targetIdx);
      if (targetPhrase) {
        const adjustedX = targetPhrase.containerLeftEdge + (overflow % phraseWidth);
        return worldXToTimestamp(adjustedX, phraseLayout, targetPhrase);
      }
      const lastPhrase = phraseLayout[phraseLayout.length - 1];
      return worldXToTimestamp(lastPhrase.containerRightEdge, phraseLayout, lastPhrase);
    }

    if (worldX < phrase.containerLeftEdge) {
      const underflow = phrase.containerLeftEdge - worldX;
      const extraPhrases = Math.floor(underflow / phraseWidth) + 1;
      const targetIdx = phrase.phraseIndex - extraPhrases;
      const targetPhrase = phraseLayout.find((p) => p.phraseIndex === targetIdx);
      if (targetPhrase) {
        const adjustedX = targetPhrase.containerRightEdge - (underflow % phraseWidth);
        return worldXToTimestamp(adjustedX, phraseLayout, targetPhrase);
      }
      const firstPhrase = phraseLayout[0];
      return worldXToTimestamp(firstPhrase.containerLeftEdge, phraseLayout, firstPhrase);
    }

    return worldXToTimestamp(worldX, phraseLayout, phrase);
  }

  // ---------------------------------------------------------------------------
  // Drag Handlers
  // ---------------------------------------------------------------------------

  /**
   * Compute the smallest allowed bracket-end timestamp given the current
   * start, such that the bracket still contains MINIMUM_NOTES_IN_BRACKET
   * distinct onsets. Returns null when there aren't enough onsets at or
   * after `currentStart` to form a valid bracket.
   */
  private computeMinimumEndForStart(currentStart: number): number | null {
    const noteOnsets = this.noteData?.noteOnsets ?? [];
    const maxOffsetByOnset = this.noteData?.maxOffsetByOnset;
    if (!maxOffsetByOnset || noteOnsets.length < MINIMUM_NOTES_IN_BRACKET) return null;

    const onsetsAtOrAfterStart = noteOnsets.filter((onset) => onset >= currentStart - 1e-6);
    if (onsetsAtOrAfterStart.length < MINIMUM_NOTES_IN_BRACKET) return null;

    const lastRequiredOnset = onsetsAtOrAfterStart[MINIMUM_NOTES_IN_BRACKET - 1];
    const offset = maxOffsetByOnset.get(lastRequiredOnset);
    return offset !== undefined ? offset : null;
  }

  /**
   * Compute the largest allowed bracket-start timestamp given the current
   * end, such that the bracket still contains MINIMUM_NOTES_IN_BRACKET
   * distinct onsets. Returns null when there aren't enough onsets fully
   * contained at or before `currentEnd`.
   */
  private computeMaximumStartForEnd(currentEnd: number): number | null {
    const noteOnsets = this.noteData?.noteOnsets ?? [];
    const maxOffsetByOnset = this.noteData?.maxOffsetByOnset;
    if (!maxOffsetByOnset || noteOnsets.length < MINIMUM_NOTES_IN_BRACKET) return null;

    const onsetsContainedAtOrBeforeEnd = noteOnsets.filter((onset) => {
      const offset = maxOffsetByOnset.get(onset);
      return offset !== undefined && offset <= currentEnd + 1e-6;
    });
    if (onsetsContainedAtOrBeforeEnd.length < MINIMUM_NOTES_IN_BRACKET) return null;

    return onsetsContainedAtOrBeforeEnd[
      onsetsContainedAtOrBeforeEnd.length - MINIMUM_NOTES_IN_BRACKET
    ];
  }

  /**
   * Handle dragging of the start handle. Converts world X to a timestamp
   * using the phrase at the pointer's Y position, then either shrinks the
   * bracket (clamped to the 2-note minimum) or — when dragged past the end —
   * shifts the whole bracket forward, preserving width. The shift-on-past-end
   * gesture is intentional (the only way to drag the bracket forward via the
   * start handle when its previous width is already as small as it can be).
   */
  private handleStartHandleDrag(worldX: number, worldY: number): void {
    const rawTimestamp = this.worldPositionToTimestamp(worldX, worldY);

    // Snap to nearest onset during drag so the visual matches what
    // finalizeDrag will produce — no surprise jump on release.
    const noteOnsets = this.noteData?.noteOnsets ?? [];
    const snappedTimestamp =
      noteOnsets.length > 0 ? snapToNearestNoteOnset(rawTimestamp, noteOnsets) : rawTimestamp;

    if (snappedTimestamp >= this.currentEndTimestamp) {
      // Shift path: start dragged past end → shift the entire bracket
      // forward, preserving previous width. Only fires on an explicit
      // past-end drag, never on mid-shrink.
      const previousWidth = this.previousEndTimestamp - this.previousStartTimestamp;
      this.currentStartTimestamp = snappedTimestamp;
      this.currentEndTimestamp = snappedTimestamp + previousWidth;
    } else {
      // Shrink path: keep the end where it is, clamp the start so the
      // bracket retains at least MINIMUM_NOTES_IN_BRACKET onsets.
      const maximumStart = this.computeMaximumStartForEnd(this.currentEndTimestamp);
      const clampedStart =
        maximumStart !== null && snappedTimestamp > maximumStart ? maximumStart : snappedTimestamp;
      this.currentStartTimestamp = clampedStart;
    }

    this.renderBracketAndOverlay(
      this.currentStartTimestamp,
      this.currentEndTimestamp,
      this.cachedPhraseLayout,
    );
    this.onBracketDragging?.(this.currentStartTimestamp);
  }

  /**
   * Handle dragging of the end handle. Converts world X to a timestamp,
   * snaps it to the last note's offset, then clamps it so the bracket
   * retains at least the 2-note minimum from the current start. Closing
   * the loop never pushes the start handle back — it just stops at the
   * minimum width.
   */
  private handleEndHandleDrag(worldX: number, worldY: number): void {
    const rawTimestamp = this.worldPositionToTimestamp(worldX, worldY);

    // Snap to the offset of the last onset at or before the raw timestamp,
    // matching finalizeDrag so there's no jump on release.
    const noteOnsets = this.noteData?.noteOnsets ?? [];
    const maxOffsetByOnset = this.noteData?.maxOffsetByOnset;
    let snappedEnd = rawTimestamp;
    if (noteOnsets.length > 0 && maxOffsetByOnset) {
      let targetOnsetIdx = -1;
      for (let i = noteOnsets.length - 1; i >= 0; i--) {
        if (noteOnsets[i] <= rawTimestamp) {
          targetOnsetIdx = i;
          break;
        }
      }
      if (targetOnsetIdx !== -1) {
        const targetOnset = noteOnsets[targetOnsetIdx];
        const offset = maxOffsetByOnset.get(targetOnset);
        if (offset !== undefined) {
          snappedEnd = offset;
        }
      }
    }

    // Clamp to the 2-note minimum from the current start. Never moves the
    // start: closing the loop just stops at the minimum, it does not push
    // the bracket back.
    const minimumEnd = this.computeMinimumEndForStart(this.currentStartTimestamp);
    if (minimumEnd !== null && snappedEnd < minimumEnd) {
      snappedEnd = minimumEnd;
    }

    this.currentEndTimestamp = snappedEnd;

    this.renderBracketAndOverlay(
      this.currentStartTimestamp,
      this.currentEndTimestamp,
      this.cachedPhraseLayout,
    );
    // Cursor follows the bracket start — snap so it lands on a real onset.
    const snappedStart =
      noteOnsets.length > 0
        ? snapToNearestNoteOnset(this.currentStartTimestamp, noteOnsets)
        : this.currentStartTimestamp;
    this.onBracketDragging?.(snappedStart);
  }

  /**
   * Handle dragging of the bracket body. Translates the entire bracket
   * by the delta between the current pointer position and the drag start.
   * Uses the phrase at the pointer's Y position for accurate X-to-timestamp mapping.
   */
  private handleBodyDrag(currentWorldX: number, currentWorldY: number): void {
    const deltaX = currentWorldX - this.bodyDragStartWorldX;
    const bracketDuration = this.bodyDragStartEndTimestamp - this.bodyDragStartStartTimestamp;

    // Convert the X delta to a timestamp delta, wrapping across phrases
    const startWorldX = timestampToWorldX(
      this.bodyDragStartStartTimestamp,
      this.cachedPhraseLayout,
    );
    const newStartWorldX = startWorldX + deltaX;
    const newStartTimestamp = this.worldPositionToTimestamp(newStartWorldX, currentWorldY);
    const newEndTimestamp = newStartTimestamp + bracketDuration;

    // Clamp to available note range
    const allOnsets = this.noteData?.noteOnsets ?? [];
    if (allOnsets.length === 0) return;

    const minTimestamp = allOnsets[0];
    const maxTimestamp = allOnsets[allOnsets.length - 1];

    if (newStartTimestamp < minTimestamp) {
      this.currentStartTimestamp = minTimestamp;
      this.currentEndTimestamp = minTimestamp + bracketDuration;
    } else if (newEndTimestamp > maxTimestamp) {
      this.currentEndTimestamp = maxTimestamp;
      this.currentStartTimestamp = maxTimestamp - bracketDuration;
    } else {
      this.currentStartTimestamp = newStartTimestamp;
      this.currentEndTimestamp = newEndTimestamp;
    }

    this.renderBracketAndOverlay(
      this.currentStartTimestamp,
      this.currentEndTimestamp,
      this.cachedPhraseLayout,
    );
    const bodyNoteOnsets = this.noteData?.noteOnsets ?? [];
    const bodySnappedStart =
      bodyNoteOnsets.length > 0
        ? snapToNearestNoteOnset(this.currentStartTimestamp, bodyNoteOnsets)
        : this.currentStartTimestamp;
    this.onBracketDragging?.(bodySnappedStart);
  }

  // ---------------------------------------------------------------------------
  // Edge-scroll during drag
  // ---------------------------------------------------------------------------

  /**
   * Start a continuous edge-scroll loop that runs every frame during drag.
   * Checks the active pointer position and scrolls the camera when it's
   * near the top or bottom edge of the viewport — even if the pointer
   * isn't moving (drag events only fire on movement).
   */
  private edgeScrollTickCount: number = 0;

  private startEdgeScrollLoop(): void {
    this.stopEdgeScrollLoop();
    this.edgeScrollTickCount = 0;
    console.log(`[EDGE-SCROLL] startEdgeScrollLoop — isDragging=${this.isDragging()}`);
    this.edgeScrollInterval = setInterval(() => {
      this.edgeScrollTickCount++;
      const pointer = this.scene.input.activePointer;
      if (!this.isDragging()) {
        console.log(`[EDGE-SCROLL] stopping — no longer dragging`);
        this.stopEdgeScrollLoop();
        return;
      }

      const camera = this.scene.cameras.main;
      // Use the raw browser pointer position (CSS pixels relative to viewport)
      // instead of Phaser's pointer.y (game coords). This correctly accounts
      // for headers, footers, and other UI overlapping the canvas.
      // On Capacitor (iOS/Android), use TouchEvent.touches[]; on web, use
      // the PointerEvent/MouseEvent clientY directly.
      const evt = pointer.event;
      const browserY = isCapacitorNative()
        ? 'touches' in evt && (evt as TouchEvent).touches.length > 0
          ? (evt as TouchEvent).touches[0].clientY
          : pointer.y
        : ((evt as PointerEvent).clientY ?? pointer.y);
      const viewportHeight = window.innerHeight;
      const pointerY = browserY;
      const dpr = window.devicePixelRatio ?? 1;
      const edgeZone = EDGE_SCROLL_ZONE_PX; // CSS pixels, no DPR scaling needed
      const scrollSpeed = EDGE_SCROLL_SPEED * dpr; // scroll in game pixels

      const cameraBounds = camera.getBounds();
      const maxScrollY = Math.max(0, cameraBounds.height - camera.height);

      // Log every 30 ticks (~0.5s) so we can see if it's running
      if (this.edgeScrollTickCount % 30 === 0) {
        console.log(
          `[EDGE-SCROLL] tick#${this.edgeScrollTickCount} pointerY=${pointerY.toFixed(0)} viewportH=${viewportHeight.toFixed(0)} edgeZone=${edgeZone.toFixed(0)} bottomThreshold=${(viewportHeight - edgeZone).toFixed(0)} scrollY=${camera.scrollY.toFixed(0)} maxScrollY=${maxScrollY.toFixed(0)} dpr=${dpr}`,
        );
      }

      if (pointerY < edgeZone) {
        const intensity = 1 - pointerY / edgeZone;
        camera.scrollY = Math.max(0, camera.scrollY - scrollSpeed * intensity);
      } else if (pointerY > viewportHeight - edgeZone) {
        const distFromBottom = viewportHeight - pointerY;
        const intensity = 1 - distFromBottom / edgeZone;
        camera.scrollY = Math.min(maxScrollY, camera.scrollY + scrollSpeed * intensity);
      }
    }, 16);
  }

  private stopEdgeScrollLoop(): void {
    if (this.edgeScrollInterval !== null) {
      clearInterval(this.edgeScrollInterval);
      this.edgeScrollInterval = null;
    }
  }

  /**
   * Swap the drag target between start and end handle. Called when a handle
   * is dragged past the other, so the user continues dragging the opposite edge.
   */
  private swapDragTarget(): void {
    if (this.isDraggingStartHandle) {
      this.isDraggingStartHandle = false;
      this.isDraggingEndHandle = true;
    } else if (this.isDraggingEndHandle) {
      this.isDraggingEndHandle = false;
      this.isDraggingStartHandle = true;
    }
  }

  /**
   * Finalize a drag operation: snap edges to note boundaries, enforce
   * constraints, and notify the scene via the callback (debounced).
   */
  private finalizeDrag(): void {
    if (!this.noteData) return;

    const { noteOnsets, noteOffsets, maxOffsetByOnset } = this.noteData;
    if (noteOnsets.length === 0 || noteOffsets.length === 0) return;

    // Only re-snap the handle being dragged. Re-snapping the OTHER handle
    // causes drift: the end is stored as a maxOffset (e.g. 6.75), and
    // re-snapping maps it to the nearest onset (6.75) then to THAT onset's
    // maxOffset (7.25) — creeping forward by one note on every drag.
    let snappedStart: number;
    let snappedEnd: number;

    if (this.isDraggingStartHandle) {
      snappedStart = snapToNearestNoteOnset(this.currentStartTimestamp, noteOnsets);

      // If the end was shifted during drag (start dragged past end),
      // re-snap it so it aligns with actual note boundaries. Without this,
      // the raw shifted end preserves the previous width which may include
      // a pickup measure duration that doesn't apply at the new position.
      const endWasShifted = Math.abs(this.currentEndTimestamp - this.previousEndTimestamp) > 0.01;
      if (endWasShifted) {
        const rawShiftedEnd = this.currentEndTimestamp;
        // Re-snap the shifted end to the nearest note boundary. No phrase-
        // boundary cap: cross-phrase looping is allowed, so the bracket may
        // extend into subsequent phrases.

        let reSnapIdx = -1;
        for (let i = noteOnsets.length - 1; i >= 0; i--) {
          if (noteOnsets[i] < rawShiftedEnd) {
            reSnapIdx = i;
            break;
          }
        }
        const reSnapOnset =
          reSnapIdx !== -1 ? noteOnsets[reSnapIdx] : noteOnsets[noteOnsets.length - 1];
        const reSnapOffset = maxOffsetByOnset.get(reSnapOnset);
        snappedEnd = reSnapOffset !== undefined ? reSnapOffset : rawShiftedEnd;
        console.log(
          `[DRAG-DBG] finalizeDrag START-HANDLE — re-snapped shifted end: rawShiftedEnd=${rawShiftedEnd} reSnapOnset=${reSnapOnset} snappedEnd=${snappedEnd}`,
        );
      } else {
        snappedEnd = this.currentEndTimestamp;
      }

      console.log(
        `[DRAG-DBG] finalizeDrag START-HANDLE — rawStart=${this.currentStartTimestamp} snappedStart=${snappedStart} snappedEnd=${snappedEnd} endWasShifted=${endWasShifted}`,
      );
    } else if (this.isDraggingEndHandle) {
      snappedStart = this.currentStartTimestamp;
      const rawEnd = this.currentEndTimestamp;

      // For the end handle, use "last onset strictly before rawEnd" instead
      // of nearest-onset. This matches the drag gesture: the user drags the
      // handle rightward past notes they want to include. The bracket should
      // include all notes they've passed over, and end when the last passed
      // note finishes sounding. Nearest-onset can jump FORWARD to a note the
      // user hasn't reached yet (e.g., rawEnd=3.89 snapping to onset 4.0
      // instead of 3.75), which feels wrong. Use strict < because the end
      // timestamp is already snapped to a note offset during drag, which
      // often equals the next note's onset. Using <= would include that
      // next note and overshoot by one.
      let targetOnsetIdx = -1;
      for (let i = noteOnsets.length - 1; i >= 0; i--) {
        if (noteOnsets[i] < rawEnd) {
          targetOnsetIdx = i;
          break;
        }
      }
      const targetOnset = targetOnsetIdx !== -1 ? noteOnsets[targetOnsetIdx] : noteOnsets[0];
      const chordMaxOffset = maxOffsetByOnset.get(targetOnset);
      snappedEnd =
        chordMaxOffset !== undefined ? chordMaxOffset : noteOffsets[noteOffsets.length - 1];

      // Diagnostic: show nearby onsets and which one was picked
      const nearbyOnsets = noteOnsets
        .map((o, i) => ({ onset: o, dist: Math.abs(o - rawEnd), idx: i }))
        .filter((o) => o.dist < 1.0)
        .sort((a, b) => a.dist - b.dist);
      console.log(
        `[DRAG-DBG] finalizeDrag END-HANDLE — rawEnd=${rawEnd.toFixed(4)} targetOnset=${targetOnset} chordMaxOffset=${chordMaxOffset} snappedEnd=${snappedEnd} keptStart=${snappedStart}`,
      );
      console.log(
        `[DRAG-DBG] finalizeDrag END-HANDLE — nearby onsets (within 1.0, sorted by distance): [${nearbyOnsets.map((o) => `${o.onset}(d=${o.dist.toFixed(3)})`).join(', ')}]`,
      );
    } else {
      // Body drag: snap both (start to nearest onset, end to max offset)
      snappedStart = snapToNearestNoteOnset(this.currentStartTimestamp, noteOnsets);
      const rawEnd = this.currentEndTimestamp;
      const targetOnset = snapToNearestNoteOnset(rawEnd, noteOnsets);
      const chordMaxOffset = maxOffsetByOnset.get(targetOnset);
      snappedEnd =
        chordMaxOffset !== undefined ? chordMaxOffset : noteOffsets[noteOffsets.length - 1];
      console.log(
        `[DRAG-DBG] finalizeDrag BODY — rawStart=${this.currentStartTimestamp} snappedStart=${snappedStart} rawEnd=${rawEnd} targetOnset=${targetOnset} snappedEnd=${snappedEnd}`,
      );
    }

    console.log(`[DRAG-DBG] finalizeDrag — ALL onsets: [${noteOnsets.join(', ')}]`);

    // Ensure start < end after snapping, and clamp start to 0
    const finalStart = Math.max(0, Math.min(snappedStart, snappedEnd));
    const finalEnd = Math.max(snappedStart, snappedEnd);

    // Validate constraints: minimum notes and not rests-only
    const isValid = hasMinimumNotes(noteOnsets, finalStart, finalEnd);

    console.log(
      `[DRAG-DBG] finalizeDrag — finalStart=${finalStart} finalEnd=${finalEnd} isValid=${isValid}`,
    );

    if (isValid) {
      this.currentStartTimestamp = finalStart;
      this.currentEndTimestamp = finalEnd;
      this.previousStartTimestamp = finalStart;
      this.previousEndTimestamp = finalEnd;
    } else {
      // The snapped result has too few notes (e.g., start dragged close to
      // end). Try shifting the bracket to maintain its previous width at
      // the new position instead of reverting. After shifting, re-snap the
      // end so it aligns with actual note boundaries. This prevents the
      // bracket from overshooting when the previous width included a
      // pickup measure duration that doesn't apply at the new position.
      const prevWidth = this.previousEndTimestamp - this.previousStartTimestamp;
      let shiftedStart: number;
      let shiftedEnd: number;

      if (this.isDraggingEndHandle) {
        shiftedEnd = finalEnd;
        shiftedStart = Math.max(0, finalEnd - prevWidth);
        // Re-snap start to nearest onset
        shiftedStart = snapToNearestNoteOnset(shiftedStart, noteOnsets);
      } else {
        shiftedStart = finalStart;
        const rawShiftedEnd = finalStart + prevWidth;
        // Re-snap end: find last onset before the shifted end, use its offset
        let reSnapIdx = -1;
        for (let i = noteOnsets.length - 1; i >= 0; i--) {
          if (noteOnsets[i] < rawShiftedEnd) {
            reSnapIdx = i;
            break;
          }
        }
        const reSnapOnset =
          reSnapIdx !== -1 ? noteOnsets[reSnapIdx] : noteOnsets[noteOnsets.length - 1];
        const reSnapOffset = maxOffsetByOnset.get(reSnapOnset);
        shiftedEnd = reSnapOffset !== undefined ? reSnapOffset : rawShiftedEnd;
      }

      const shiftedValid = hasMinimumNotes(
        noteOnsets,
        Math.min(shiftedStart, shiftedEnd),
        Math.max(shiftedStart, shiftedEnd),
      );

      if (shiftedValid) {
        console.log(
          `[DRAG-DBG] finalizeDrag — SHIFTED bracket: start=${Math.min(shiftedStart, shiftedEnd)} end=${Math.max(shiftedStart, shiftedEnd)}`,
        );
        this.currentStartTimestamp = Math.min(shiftedStart, shiftedEnd);
        this.currentEndTimestamp = Math.max(shiftedStart, shiftedEnd);
        this.previousStartTimestamp = this.currentStartTimestamp;
        this.previousEndTimestamp = this.currentEndTimestamp;
      } else {
        console.log(
          `[DRAG-DBG] finalizeDrag — REVERTED to previous start=${this.previousStartTimestamp} end=${this.previousEndTimestamp}`,
        );
        this.currentStartTimestamp = this.previousStartTimestamp;
        this.currentEndTimestamp = this.previousEndTimestamp;
      }
    }

    this.renderBracketAndOverlay(
      this.currentStartTimestamp,
      this.currentEndTimestamp,
      this.cachedPhraseLayout,
    );

    this.debounceBracketChanged();
  }

  // ---------------------------------------------------------------------------
  // Debouncing
  // ---------------------------------------------------------------------------

  /**
   * Schedule the bracket changed callback to fire after DEBOUNCE_DELAY_MS.
   * Resets the timer on each call, so rapid adjustments only trigger one callback.
   */
  private debounceBracketChanged(): void {
    this.clearDebounceTimer();

    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      if (this.onBracketChanged) {
        this.onBracketChanged(this.currentStartTimestamp, this.currentEndTimestamp);
      }
    }, DEBOUNCE_DELAY_MS);
  }

  /**
   * Clear any pending debounce timer.
   */
  private clearDebounceTimer(): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  // ---------------------------------------------------------------------------
  // State Management
  // ---------------------------------------------------------------------------

  /**
   * Store the current position as the previous valid position,
   * used for rollback if a drag violates constraints.
   */
  private storePreviousPosition(): void {
    this.previousStartTimestamp = this.currentStartTimestamp;
    this.previousEndTimestamp = this.currentEndTimestamp;
  }

  /**
   * Destroy the bracket body zone if it exists.
   */
  private destroyBodyZone(): void {
    if (this.bracketBodyZone) {
      this.bracketBodyZone.removeAllListeners();
      this.bracketBodyZone.destroy();
      this.bracketBodyZone = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  /**
   * Compute bracket rows from timestamps and phrase layout, then draw
   * the bracket shape, handles, and dimming overlay.
   */
  private renderBracketAndOverlay(
    startTimestamp: number,
    endTimestamp: number,
    phraseLayout: PhraseLayoutInfo[],
  ): void {
    this.bracketGraphics.clear();
    this.dimmingGraphics.clear();
    this.startHandle.clear();
    this.endHandle.clear();

    const bracketRows = this.computeBracketRows(startTimestamp, endTimestamp, phraseLayout);
    this.cachedBracketRows = bracketRows;
    if (bracketRows.length === 0) return;

    this.drawBracketShape(bracketRows);
    this.drawHandles(bracketRows);
    this.drawDimmingOverlay(bracketRows, phraseLayout);
    this.setupBodyZone(bracketRows);
    this.updateHandleHitAreas(bracketRows);
  }

  /**
   * Update handle hit areas to match the current rendered positions.
   * This ensures the interactive zone moves with the handle graphic.
   */
  private updateHandleHitAreas(rows: BracketRow[]): void {
    if (rows.length === 0) return;

    const firstRow = rows[0];
    const lastRow = rows[rows.length - 1];
    const hitAreaSize =
      HANDLE_RADIUS_BASE * handleDprScale() * 2 +
      HANDLE_HIT_AREA_PADDING_BASE * handleDprScale() * 2;
    const startHandleCenterX = firstRow.startX;
    const startHandleCenterY = (firstRow.topY + firstRow.bottomY) / 2;

    this.startHandle.setPosition(startHandleCenterX, startHandleCenterY);
    this.startHandle.input!.hitArea = new Phaser.Geom.Rectangle(
      -hitAreaSize / 2,
      -hitAreaSize / 2,
      hitAreaSize,
      hitAreaSize,
    );
    const endHandleCenterX = lastRow.endX;
    const endHandleCenterY = (lastRow.topY + lastRow.bottomY) / 2;

    this.endHandle.setPosition(endHandleCenterX, endHandleCenterY);
    this.endHandle.input!.hitArea = new Phaser.Geom.Rectangle(
      -hitAreaSize / 2,
      -hitAreaSize / 2,
      hitAreaSize,
      hitAreaSize,
    );
  }

  /**
   * Compute the bracket rows — each row represents one phrase line that
   * the bracket spans. A bracket that spans multiple phrases produces
   * multiple rows.
   */
  private computeBracketRows(
    startTimestamp: number,
    endTimestamp: number,
    phraseLayout: PhraseLayoutInfo[],
  ): BracketRow[] {
    const rows: BracketRow[] = [];

    const phrasesInRange = phraseLayout.filter((phrase) => {
      const phraseTimestamps = phrase.notePositions.map((note) => note.timestamp);
      if (phraseTimestamps.length === 0) return false;
      const phraseMinTimestamp = Math.min(...phraseTimestamps);
      const phraseMaxTimestamp = Math.max(...phraseTimestamps);
      return phraseMaxTimestamp >= startTimestamp && phraseMinTimestamp < endTimestamp;
    });

    phrasesInRange.forEach((phrase) => {
      // Use strict less-than for the end boundary because endTimestamp is a
      // note *offset* (end of the last note's duration). A note whose onset
      // equals endTimestamp starts at the boundary and belongs to the next
      // section, not this bracket.
      const notesInRange = phrase.notePositions.filter(
        (note) => note.timestamp >= startTimestamp && note.timestamp < endTimestamp,
      );

      if (notesInRange.length === 0) return;

      const allPhraseTimestamps = phrase.notePositions.map((note) => note.timestamp);
      const phraseMinTimestamp = Math.min(...allPhraseTimestamps);
      const phraseMaxTimestamp = Math.max(...allPhraseTimestamps);

      const bracketIncludesFirstNote = phraseMinTimestamp >= startTimestamp;
      // Use strict < to match the notesInRange filter (timestamp < endTimestamp).
      // With <=, a snappedEnd that equals the last note's onset (e.g. both 5.75)
      // would incorrectly extend the bracket to the container right edge even
      // though that note is NOT in the bracket.
      const bracketIncludesLastNote = phraseMaxTimestamp < endTimestamp;

      const isFirstPhrase = phrase === phrasesInRange[0];
      const isLastPhrase = phrase === phrasesInRange[phrasesInRange.length - 1];

      const noteXPositions = notesInRange.map((note) => note.x);

      console.log(
        `[BRACKET DEBUG] phrase ${phrase.phraseIndex}: phraseMin=${phraseMinTimestamp} phraseMax=${phraseMaxTimestamp} startTs=${startTimestamp} endTs=${endTimestamp} includesFirst=${bracketIncludesFirstNote} includesLast=${bracketIncludesLastNote} isFirst=${isFirstPhrase} isLast=${isLastPhrase} containerLeft=${phrase.containerLeftEdge} containerRight=${phrase.containerRightEdge} noteXmin=${Math.min(...noteXPositions)} noteXmax=${Math.max(...noteXPositions)}`,
      );

      // For continuation rows (non-first phrases in a multi-row bracket),
      // extend to the phrase container left edge for a clean full-width look.
      // For the first phrase: extend to the container left edge ONLY if the
      // bracket includes the first note AND that note starts at beat 1 (no
      // rests precede it). If rests precede the first note, use the note's X
      // so the bracket doesn't visually cover the rests.
      // This applies on both desktop and mobile — the left container edge is
      // close to the first note (covers clef/time signature cleanly).
      const phraseStartsWithRests =
        notesInRange.length > 0 && notesInRange[0].timestamp > phrase.phraseStartTimestamp + 0.001;
      const extendToLeftEdge =
        !isFirstPhrase || (bracketIncludesFirstNote && !phraseStartsWithRests);
      const startX = extendToLeftEdge
        ? phrase.containerLeftEdge
        : Math.min(...noteXPositions) - BRACKET_HORIZONTAL_PADDING;

      // Same container-edge logic for the right side.
      // Desktop/iPad: extend to containerRightEdge when the bracket includes
      // the last note, or for non-last phrases in a multi-row bracket.
      // Mobile: always derive endX from note positions (see startX comment).
      let endX: number;
      if (!this.mobileView && (bracketIncludesLastNote || !isLastPhrase)) {
        endX = phrase.containerRightEdge;
        console.log(
          `[BRACKET-EDGE] phrase ${phrase.phraseIndex}: using containerRightEdge=${endX} (includesLast=${bracketIncludesLastNote} isLast=${isLastPhrase})`,
        );
      } else {
        const lastIncludedX = Math.max(...noteXPositions);
        const firstExcludedNote = phrase.notePositions.find(
          (note) => note.timestamp >= endTimestamp,
        );

        console.log(
          `[BRACKET-EDGE] phrase ${phrase.phraseIndex}: lastIncludedX=${lastIncludedX.toFixed(1)} firstExcludedX=${firstExcludedNote?.x.toFixed(1) ?? 'none'} firstExcludedTs=${firstExcludedNote?.timestamp ?? 'none'} endTimestamp=${endTimestamp}`,
        );
        console.log(
          `[BRACKET-EDGE] phrase ${phrase.phraseIndex}: barlineXPositions=[${phrase.measureBarlineXPositions.map((x) => x.toFixed(1)).join(', ')}]`,
        );

        // Check if a barline falls between the last included note and the
        // first excluded note (the gap where the measure boundary is).
        const barlineInGap = firstExcludedNote
          ? phrase.measureBarlineXPositions.find(
              (bx) => bx > lastIncludedX && bx < firstExcludedNote.x,
            )
          : phrase.measureBarlineXPositions.find((bx) => bx > lastIncludedX);

        // Check if the last included note is the first note after a barline.
        // Use timestamps to avoid chord x-position mismatches.
        const lastIncludedNote = notesInRange[notesInRange.length - 1];
        const lastIncludedTs = lastIncludedNote?.timestamp;
        const barlineJustBeforeLastNote = phrase.measureBarlineXPositions.find(
          (bx) => bx <= lastIncludedX && lastIncludedX - bx < 80,
        );
        const uniqueTimestampsBetween =
          barlineJustBeforeLastNote !== undefined
            ? new Set(
                notesInRange
                  .filter((n) => n.x > barlineJustBeforeLastNote && n.timestamp !== lastIncludedTs)
                  .map((n) => n.timestamp),
              )
            : new Set<number>();
        const isFirstNoteAfterInternalBarline =
          barlineJustBeforeLastNote !== undefined &&
          notesInRange.length > 0 &&
          uniqueTimestampsBetween.size === 0;

        // Check if the last included note is the very first note of this phrase
        const firstNoteInPhrase = phrase.notePositions[0];
        const isFirstNoteInPhrase =
          firstNoteInPhrase !== undefined && Math.abs(lastIncludedX - firstNoteInPhrase.x) < 5;

        const isFirstNoteAfterBarline = isFirstNoteAfterInternalBarline || isFirstNoteInPhrase;
        const snapBarlineX = isFirstNoteInPhrase
          ? phrase.containerLeftEdge
          : barlineJustBeforeLastNote;

        console.log(
          `[BRACKET-EDGE] phrase ${phrase.phraseIndex}: barlineInGap=${barlineInGap?.toFixed(1) ?? 'none'} barlineJustBefore=${barlineJustBeforeLastNote?.toFixed(1) ?? 'none'} isFirstInPhrase=${isFirstNoteInPhrase} isFirstAfterBarline=${isFirstNoteAfterBarline} lastIncludedX=${lastIncludedX.toFixed(1)} lastIncludedTs=${lastIncludedTs} notesInRange=${notesInRange.length}`,
        );

        if (barlineInGap !== undefined) {
          // Barline between last included and first excluded — extend to it
          endX = barlineInGap;
          console.log(
            `[BRACKET-EDGE] phrase ${phrase.phraseIndex}: PATH=barline-in-gap endX=${endX.toFixed(1)}`,
          );
        } else if (firstExcludedNote) {
          // Position the bracket end just before the first excluded note's
          // head so it's visually clear that note is NOT part of the loop.
          // Using the excluded note's X minus padding (rather than the last
          // included note's X) prevents the bracket from appearing to "split"
          // the boundary note.
          endX = firstExcludedNote.x - BRACKET_HORIZONTAL_PADDING;
          console.log(
            `[BRACKET-EDGE] phrase ${phrase.phraseIndex}: PATH=before-excluded endX=${endX.toFixed(1)} firstExcludedX=${firstExcludedNote.x.toFixed(1)}`,
          );
        } else {
          // No excluded note on this phrase — use last included note's X
          endX = lastIncludedX + BRACKET_HORIZONTAL_PADDING;
          console.log(
            `[BRACKET-EDGE] phrase ${phrase.phraseIndex}: PATH=on-note endX=${endX.toFixed(1)}`,
          );
        }
      }

      rows.push({
        startX,
        endX,
        topY: phrase.y,
        bottomY: phrase.y + phrase.height,
      });
    });

    // On mobile, phrases are laid out end-to-end horizontally so a multi-row
    // bracket is really one contiguous rectangle. Merge into a single row to
    // avoid the staircase polygon and its doubled borders at phrase junctions.
    if (this.mobileView && rows.length > 1) {
      const merged: BracketRow = {
        startX: rows[0].startX,
        endX: rows[rows.length - 1].endX,
        topY: Math.min(...rows.map((r) => r.topY)),
        bottomY: Math.max(...rows.map((r) => r.bottomY)),
      };
      return [merged];
    }

    return rows;
  }

  /**
   * Draw the bracket shape as a single continuous outline around the
   * entire selection. For multi-row brackets the outline follows the
   * staircase polygon computed by computeBracketPolygon, so there are
   * no doubled-up borders at row intersections.
   */
  private drawBracketShape(rows: BracketRow[]): void {
    if (rows.length === 0) return;

    const vertices = this.computeBracketPolygon(rows);
    if (vertices.length < 3) return;

    this.bracketGraphics.lineStyle(BRACKET_LINE_WIDTH, BRACKET_COLOR, BRACKET_ALPHA);
    this.bracketGraphics.beginPath();
    this.bracketGraphics.moveTo(vertices[0].x, vertices[0].y);
    for (let i = 1; i < vertices.length; i++) {
      this.bracketGraphics.lineTo(vertices[i].x, vertices[i].y);
    }
    this.bracketGraphics.closePath();
    this.bracketGraphics.strokePath();
  }

  /**
   * Draw handles at the bracket start and end positions.
   * Rendered as small rounded rectangles at the vertical center of the
   * first and last bracket rows. Drawn relative to (0,0) since the
   * handle's position is set via setPosition().
   */
  private drawHandles(rows: BracketRow[]): void {
    if (rows.length === 0) return;

    // Both handles are identical circular buttons with < > chevrons.
    // Drawn at local origin (0,0); positioned via setPosition() in
    // updateHandleHitAreas().
    this.drawCircularHandle(this.startHandle);
    this.drawCircularHandle(this.endHandle);
  }

  /**
   * Draw a circular grab handle with left and right chevrons (< >) inside.
   * Matches the Figma design: filled circle with white chevron arrows.
   */
  private drawCircularHandle(handle: Phaser.GameObjects.Graphics): void {
    const s = handleDprScale();
    // On mobile the camera zoom amplifies world-space graphics, making
    // full-size handles oversized. Scale them down to compensate.
    const mobileScale = this.mobileView ? 0.55 : 1;
    const radius = HANDLE_RADIUS_BASE * s * mobileScale;
    const chevronLineWidth = CHEVRON_LINE_WIDTH_BASE * s * mobileScale;
    const chevronHeight = CHEVRON_HEIGHT_BASE * s * mobileScale;
    const chevronWidth = CHEVRON_WIDTH_BASE * s * mobileScale;
    const chevronGap = CHEVRON_GAP_BASE * s * mobileScale;

    // Filled circle background
    handle.fillStyle(HANDLE_COLOR, HANDLE_ALPHA);
    handle.fillCircle(0, 0, radius);

    // Draw < > chevrons centered in the circle
    handle.lineStyle(chevronLineWidth, CHEVRON_COLOR, CHEVRON_ALPHA);

    const halfHeight = chevronHeight / 2;

    // Left chevron (<) — offset to the left of center
    const leftTipX = -chevronGap / 2 - chevronWidth;
    const leftArmX = -chevronGap / 2;
    handle.beginPath();
    handle.moveTo(leftArmX, -halfHeight);
    handle.lineTo(leftTipX, 0);
    handle.lineTo(leftArmX, halfHeight);
    handle.strokePath();

    // Right chevron (>) — offset to the right of center
    const rightTipX = chevronGap / 2 + chevronWidth;
    const rightArmX = chevronGap / 2;
    handle.beginPath();
    handle.moveTo(rightArmX, -halfHeight);
    handle.lineTo(rightTipX, 0);
    handle.lineTo(rightArmX, halfHeight);
    handle.strokePath();
  }

  /**
   * Build a clockwise polygon that traces the outline of the bracket
   * selection across one or more rows. For multi-row brackets the shape
   * is a staircase: each row contributes its own startX/endX and the
   * gap between rows is bridged by vertical segments.
   */
  private computeBracketPolygon(rows: BracketRow[]): Array<{ x: number; y: number }> {
    if (rows.length === 0) return [];

    const v: Array<{ x: number; y: number }> = [];

    // Top edge of first row (left → right)
    v.push({ x: rows[0].startX, y: rows[0].topY });
    v.push({ x: rows[0].endX, y: rows[0].topY });

    // Right side going down through rows and inter-row gaps
    for (let i = 0; i < rows.length; i++) {
      v.push({ x: rows[i].endX, y: rows[i].bottomY });

      if (i < rows.length - 1) {
        v.push({ x: rows[i].endX, y: rows[i + 1].topY });
        if (Math.abs(rows[i + 1].endX - rows[i].endX) > 1) {
          v.push({ x: rows[i + 1].endX, y: rows[i + 1].topY });
        }
      }
    }

    // Bottom edge of last row (right → left)
    const last = rows[rows.length - 1];
    v.push({ x: last.startX, y: last.bottomY });

    // Left side going up through rows and inter-row gaps
    for (let i = rows.length - 1; i > 0; i--) {
      v.push({ x: rows[i].startX, y: rows[i].topY });
      v.push({ x: rows[i].startX, y: rows[i - 1].bottomY });
      if (Math.abs(rows[i - 1].startX - rows[i].startX) > 1) {
        v.push({ x: rows[i - 1].startX, y: rows[i - 1].bottomY });
      }
    }

    return v;
  }

  /**
   * Draw the dimming overlay outside the bracket region.
   *
   * Uses an inverted GeometryMask so a single full-viewport fill is
   * visible everywhere *except* inside the bracket polygon. This avoids
   * the alpha-compounding artefacts that multi-rectangle approaches
   * produce at rectangle boundaries, and it keeps the inter-row gap
   * visually connected to the bracket instead of being dimmed separately.
   */
  private drawDimmingOverlay(bracketRows: BracketRow[], phraseLayout: PhraseLayoutInfo[]): void {
    if (bracketRows.length === 0 || phraseLayout.length === 0) return;

    // Update the mask polygon to match the current bracket shape
    const vertices = this.computeBracketPolygon(bracketRows);
    this.dimmingMaskShape.clear();
    if (vertices.length < 3) return;

    this.dimmingMaskShape.fillStyle(0xffffff);
    this.dimmingMaskShape.beginPath();
    this.dimmingMaskShape.moveTo(vertices[0].x, vertices[0].y);
    for (let i = 1; i < vertices.length; i++) {
      this.dimmingMaskShape.lineTo(vertices[i].x, vertices[i].y);
    }
    this.dimmingMaskShape.closePath();
    this.dimmingMaskShape.fillPath();

    // Single full-viewport fill — the inverted mask hides the bracket area
    const camera = this.scene.cameras.main;
    const cameraBounds = camera.getBounds();
    const worldWidth = cameraBounds.width > 0 ? cameraBounds.width : camera.width;
    const worldHeight = cameraBounds.height > 0 ? cameraBounds.height : camera.height;

    this.dimmingGraphics.fillStyle(DIMMING_COLOR, DIMMING_ALPHA);
    this.dimmingGraphics.fillRect(0, 0, worldWidth, worldHeight);
  }
}
